import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { Check, HelpCircle, Loader2, TriangleAlert, X } from "lucide-react";
import { CATEGORY, PRIORITY, SENIORITY, STATUS, rErr, rPost } from "@/lib/radar";
import { cn } from "@/lib/utils";

export const RadarCtx = createContext({ openRun: () => {}, openJob: () => {}, bump: 0, refresh: () => {} });
export const useRadar = () => useContext(RadarCtx);

export const Pill = ({ cls, children, testid, title }) => <span title={title} className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold", cls)} data-testid={testid}>{children}</span>;
export const CategoryChip = ({ v, testid }) => (CATEGORY[v] ? <Pill cls={CATEGORY[v][1]} testid={testid}>{CATEGORY[v][0]}</Pill> : null);
export const PriorityChip = ({ v, testid }) => (PRIORITY[v] ? <Pill cls={PRIORITY[v][1]} testid={testid}>{PRIORITY[v][0]}</Pill> : null);
export const NewChip = ({ n, testid }) => <Pill cls="border-[#D4AF37] text-[#F3C649]" testid={testid}>NEW{n ? ` ${n}` : ""}</Pill>;
export const SenBadge = ({ v }) => (v ? <Pill cls="border-[#8224e3]/40 text-[#d7b6ff]">{SENIORITY[v] || v}</Pill> : null);
export const StatusPill = ({ v, title, testid }) => {
  const s = STATUS[v] || STATUS.not_run;
  return <Pill cls={s[1]} title={title} testid={testid}>{v === "running" && <Loader2 className="h-3 w-3 animate-spin" />}{s[0]}</Pill>;
};

export const Logo = ({ domain, name, size = 36 }) => {
  const [bad, setBad] = useState(false);
  const ini = String(name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return domain && !bad
    ? <img src={`https://logo.clearbit.com/${domain}`} alt="" onError={() => setBad(true)} style={{ width: size, height: size }} className="shrink-0 rounded-lg bg-white object-contain p-0.5" />
    : <span style={{ width: size, height: size }} className="flex shrink-0 items-center justify-center rounded-lg bg-[#1f1f23] font-display text-xs text-[#D4AF37]">{ini}</span>;
};

export const ScoreDot = ({ v, size = 40 }) => {
  const n = Math.max(0, Math.min(100, Number(v) || 0)), r = size / 2 - 3, c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} className="shrink-0" data-testid="radar-score-ring">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#27272A" strokeWidth="3" fill="none" />
      {v != null && <circle cx={size / 2} cy={size / 2} r={r} stroke="#D4AF37" strokeWidth="3" fill="none" strokeDasharray={c} strokeDashoffset={c * (1 - n / 100)} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} />}
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="fill-zinc-100 font-mono" fontSize={size / 3.4}>{v ?? "—"}</text>
    </svg>
  );
};

const VERDICT = {
  valid: [Check, "Valid", "text-[#34D399] border-[#10B981]/40"], risky: [TriangleAlert, "Risky", "text-amber-300 border-amber-500/40"],
  invalid: [X, "Invalid", "text-red-300 border-red-500/40"], unknown: [HelpCircle, "Unknown", "text-zinc-400 border-[#3F3F46]"],
};
// Email + verification badge (§46.1/§47.3); onChecked receives the updated person fields.
export const EmailBadge = ({ p, onChecked, compact }) => {
  const [busy, setBusy] = useState(false);
  const ck = p.email_check;
  const v = VERDICT[ck?.verdict];
  const check = async (e) => {
    e.stopPropagation(); setBusy(true);
    try { const r = await rPost(`/people/${p.id}/verify-email`, {}); onChecked?.(r); toast.success(`${r.email}: ${r.email_check?.verdict || r.email_status}`); } catch (x) { toast.error(rErr(x)); } finally { setBusy(false); }
  };
  if (!p.email) return <span className="text-[11px] text-zinc-600" data-testid={`radar-email-none-${p.id}`}>No email</span>;
  const I = v?.[0];
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1" data-testid={`radar-email-${p.id}`}>
      {!compact && <span className="truncate text-xs text-zinc-300">{p.email}</span>}
      {p.email_status === "inferred" && !ck && <Pill cls="border-amber-500/40 text-amber-300">guessed</Pill>}
      {v ? <Pill cls={v[2]} title={[ck.reason, ck.confidence_score != null && `${Number(ck.confidence_score).toFixed(2)} · ${String(ck.status || "").replace(/_/g, " ")}`].filter(Boolean).join(" — ")} testid={`radar-email-verdict-${p.id}`}><I className="h-3 w-3" />{v[1]}{ck.confidence_score != null ? ` ${Number(ck.confidence_score).toFixed(2)}` : ""}</Pill>
        : <Pill cls="border-[#3F3F46] text-zinc-500" testid={`radar-email-verdict-${p.id}`}><HelpCircle className="h-3 w-3" />Unchecked</Pill>}
      <button onClick={check} disabled={busy} className="text-[10px] text-[#D4AF37] hover:underline" data-testid={`radar-email-check-${p.id}`}>{busy ? <Loader2 className="inline h-3 w-3 animate-spin" /> : "Check"}</button>
    </span>
  );
};

export const Kpi = ({ k, v, dot, testid }) => (
  <div className="min-w-[110px] flex-1 rounded-xl border border-[#27272A] bg-[#0f0f12] px-3 py-2.5" data-testid={testid}>
    <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">{k}{dot && <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />}</p>
    <p className="font-display text-xl text-zinc-50">{v ?? "—"}</p>
  </div>
);

export const Sel = ({ value, onChange, options, label, testid, className }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cn("h-8 rounded-md border border-[#27272A] bg-[#121215] px-2 text-xs text-zinc-200", className)} data-testid={testid}>
    <option value="">{label}</option>
    {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>))}
  </select>
);
